import { availableFromText } from "@/components/vendor/ServiceAvailableFrom";
import React, { useRef, useState } from "react";
import { useAdView } from "@/lib/useAdView";
import ImageSlider from "@/components/ImageSlider";
import { Link } from "react-router-dom";
import { Heart, MapPin, Star, MessageCircle, Minus, Plus, Briefcase, Share2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSavedJobs, toggleSavedJob } from "@/lib/savedJobs";
import { resolveImageUrl, trackWhatsAppOrder, trackSponsoredClick, trackAdWhatsApp } from "@/lib/api";
import { waUrl, isNewItem } from "@/lib/whatsapp";
import { contactGate } from "@/lib/contactGate";
import { jobEnquiryMsg } from "@/lib/enquiry";
import { T } from "@/i18n";
import { toast } from "sonner";
import ShopStatusPill from "@/components/ShopStatusPill";
import { SampleBadge } from "@/components/ImageLibrary";

export const fmtKm = (d) => (d === null || d === undefined ? "Distance unavailable" : `${d} km`);
// Area + PIN line for results that belong to a different (nearby) PIN.
const NearbyArea = ({ item, testid }) => (!item.same_pincode && item.location && item.pincode
  ? <div className="text-[11px] text-amber-800 truncate mt-0.5" data-testid={testid}>{item.location} · PIN {item.pincode}</div> : null);

const NewBadge = ({ testid }) => (
  <div className="bg-sky-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={testid}><T k="card.new" /></div>
);

const orderOnWhatsApp = (product, qty) => {
  const msg = `Hello ${product.shop_name}, mujhe ${product.name} order karna hai. Price: ₹${product.price}, Quantity: ${qty}. Please confirm availability.`;
  trackWhatsAppOrder(product.id);
  trackAdWhatsApp(product, "product_listing");
  window.open(waUrl(product.shop_whatsapp, msg), "_blank", "noopener");
};

// Discount only when MRP > 0 and selling price is strictly lower; never 0%/negative.
export function priceOff(mrp, price) {
  const m = Number(mrp), p = Number(price);
  if (!Number.isFinite(m) || !Number.isFinite(p) || m <= 0 || p < 0 || p >= m) return null;
  const pct = Math.floor(((m - p) / m) * 100);
  return pct >= 1 ? { pct, save: Math.round((m - p) * 100) / 100 } : null;
}

export const SpecialNoteBadge = ({ note, className = "", testid }) => note?.trim() ? (
  <div className={`max-w-full truncate bg-slate-900/85 text-amber-300 font-bold px-2 py-0.5 rounded-full shadow-sm pointer-events-none ${className}`} title={note} data-testid={testid}>{note.trim()}</div>
) : null;

const SampleListingBadge = ({ testid }) => (
  <div className="bg-yellow-400 text-slate-900 text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm ring-1 ring-yellow-500" title="Sample / demo listing — not a real business" data-testid={testid}>Sample</div>
);

export function ProductCard({ product }) {
  const { toggleWishlist, inWishlist, user } = useApp();
  const [qty, setQty] = useState(1);
  const discount = priceOff(product.mrp, product.price)?.pct || 0;
  const adRef = useRef(null);
  useAdView(adRef, product.sponsored ? product.sponsored_ad_id : null, product.sponsored_pincode, "product_listing");

  return (
    <div ref={adRef} className="group relative bg-white border border-slate-100 rounded-2xl overflow-hidden card-hover animate-fade-up" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} onClick={() => trackSponsoredClick(product, "product_listing")} className="block relative">
        <div className="relative aspect-square overflow-hidden bg-slate-50">
          <img src={resolveImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          <SampleBadge url={product.image} className="absolute bottom-2 right-2" testid={`product-sample-${product.id}`} />
          {(discount > 0 || isNewItem(product.created_at) || product.sponsored || product.special_note || product.is_sample === true) && (
            <div className="absolute top-2 left-2 right-12 flex flex-col items-start gap-1 pointer-events-none">
              {product.is_sample === true && <SampleListingBadge testid={`product-sample-badge-${product.id}`} />}
              {product.sponsored && <div className="bg-slate-900/85 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`product-sponsored-${product.id}`}><T k="card.sponsored" /></div>}
              {discount > 0 && <div className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`product-discount-badge-${product.id}`}>{discount}% OFF</div>}
              <SpecialNoteBadge note={product.special_note} className="text-[10px]" testid={`product-note-badge-${product.id}`} />
              {isNewItem(product.created_at) && <NewBadge testid={`product-new-badge-${product.id}`} />}
            </div>
          )}
          {product.same_pincode && (
            <div className="absolute bottom-2 left-2 bg-emerald-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Same pincode
            </div>
          )}
          {product.in_stock === false && (
            <div className="absolute inset-0 bg-black/35 backdrop-blur-[1px] flex items-center justify-center" data-testid={`product-unavailable-${product.id}`}>
              <span className="bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">Unavailable</span>
            </div>
          )}
          {product.shop_status && (
            <div className="absolute bottom-2 right-2">
              <ShopStatusPill status={product.shop_status} shopId={product.shop_id} size="xs" />
            </div>
          )}
        </div>
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white transition-colors"
        aria-label="Wishlist"
        data-testid={`wishlist-toggle-${product.id}`}
      >
        <Heart className={`w-4 h-4 ${inWishlist(product.id) ? "text-red-500 fill-red-500" : "text-slate-500"}`} />
      </button>
      <div className="p-3">
        <Link to={`/product/${product.id}`} onClick={() => trackSponsoredClick(product, "product_listing")}>
          <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
        </Link>
        <Link to={`/v/${product.shop_slug || product.shop_id}`} className="block text-xs font-bold text-[rgb(var(--vb-primary-dark))] hover:underline underline-offset-2 truncate mt-0.5" data-testid={`product-shop-link-${product.id}`}>{product.shop_name}</Link>
        <NearbyArea item={product} testid={`product-area-${product.id}`} />
        <div className="flex items-center gap-2 mt-1.5 text-xs">
          <div className="flex items-center gap-0.5 bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded font-semibold">
            <Star className="w-3 h-3 fill-current" /> {product.rating}
          </div>
          <div className="flex items-center gap-0.5 text-slate-500">
            <MapPin className="w-3 h-3" /> {fmtKm(product.distance_km)}
          </div>
        </div>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="text-lg font-bold text-slate-900 font-display leading-none">₹{product.price}</div>
            {discount > 0 && <div className="text-xs text-slate-400 line-through">₹{product.mrp}</div>}
          </div>
        </div>
        {(product.shop_whatsapp || product.contact_locked) && (
          <>
            <div className="mt-2 flex items-center justify-center gap-2" data-testid={`wa-qty-${product.id}`}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} className="w-6 h-6 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40" aria-label="Decrease quantity" data-testid={`wa-qty-dec-${product.id}`}><Minus className="w-3 h-3" /></button>
              <span className="text-xs font-semibold min-w-[1.25rem] text-center" data-testid={`wa-qty-value-${product.id}`}>{qty}</span>
              <button onClick={() => setQty((q) => Math.min(99, q + 1))} className="w-6 h-6 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50" aria-label="Increase quantity" data-testid={`wa-qty-inc-${product.id}`}><Plus className="w-3 h-3" /></button>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); if (contactGate(user, product.contact_locked)) return; orderOnWhatsApp(product, qty); }}
              disabled={product.in_stock === false}
              className="mt-1.5 w-full inline-flex items-center justify-center gap-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-semibold px-2 py-1.5 transition-colors"
              data-testid={`whatsapp-order-${product.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate"><T k="card.waOrder" /></span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ServiceCard({ service }) {
  return (
    <Link to={`/service/${service.id}`} className="group relative bg-white border border-slate-100 rounded-2xl overflow-hidden card-hover animate-fade-up flex flex-col" data-testid={`service-card-${service.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-50">
        <img src={resolveImageUrl(service.image)} alt={service.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <SampleBadge url={service.image} className="absolute bottom-2 right-2" testid={`service-sample-${service.id}`} />
        {(service.same_pincode || isNewItem(service.created_at) || service.is_sample === true) && (
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            {service.is_sample === true && <SampleListingBadge testid={`service-sample-badge-${service.id}`} />}
            {service.same_pincode && (
              <div className="bg-emerald-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Nearby
              </div>
            )}
            {isNewItem(service.created_at) && <NewBadge testid={`service-new-badge-${service.id}`} />}
          </div>
        )}
        {service.available === false && (
          <div className="absolute top-2 right-2 flex flex-col items-end gap-1 max-w-[70%]">
            <span className="bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" data-testid={`service-unavailable-${service.id}`}>Unavailable</span>
            {availableFromText(service) && <span className="bg-white/95 text-amber-800 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm text-right" data-testid={`service-available-from-label-${service.id}`}>{availableFromText(service)}</span>}
          </div>
        )}
        {service.shop_status && (
          <div className="absolute bottom-2 right-2">
            <ShopStatusPill status={service.shop_status} shopId={service.shop_id} size="xs" />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-slate-900 line-clamp-1" data-testid={`service-name-${service.id}`}>{service.name}</h3>
        <div className="text-xs text-[rgb(var(--vb-primary-dark))] font-medium truncate mt-0.5" data-testid={`service-provider-${service.id}`}>{service.provider || service.shop_name}</div>
        {service.shop_name && service.provider && service.shop_name.trim() !== service.provider.trim() && <div className="text-[11px] text-slate-400 truncate" data-testid={`service-shop-${service.id}`}>at {service.shop_name}</div>}
        <NearbyArea item={service} testid={`service-area-${service.id}`} />
        {service.category && <div className="text-[10px] uppercase tracking-wider text-emerald-700 mt-1 font-semibold" data-testid={`service-category-${service.id}`}>{service.category}</div>}
        <div className="flex items-center gap-2 mt-2 text-xs">
          <div className="flex items-center gap-0.5 bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded font-semibold">
            <Star className="w-3 h-3 fill-current" /> {service.rating}
          </div>
          <div className="flex items-center gap-0.5 text-slate-500">
            <MapPin className="w-3 h-3" /> {fmtKm(service.distance_km)}
          </div>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Starts from</div>
            <div className="text-lg font-bold text-slate-900 font-display leading-none">{service.price_from == null ? <span className="text-sm">Fee as discussed</span> : `₹${service.price_from}`}</div>
            <div className="text-[10px] text-slate-500">{service.unit}</div>
          </div>
          <span className="text-xs bg-emerald-700 group-hover:bg-emerald-800 group-active:bg-emerald-900 text-white rounded-full px-3 py-1.5 font-semibold transition-colors" data-testid={`view-service-${service.id}`}>
            View
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ShopCard({ shop }) {
  const target = shop.slug ? `/v/${shop.slug}` : `/shop/${shop.id}`;
  const adRef = useRef(null);
  useAdView(adRef, shop.sponsored ? shop.sponsored_ad_id : null, shop.sponsored_pincode, "shop_listing");
  return (
    <Link ref={adRef} to={target} onClick={() => trackSponsoredClick(shop, "shop_listing")} className="group relative bg-white border border-slate-100 rounded-2xl overflow-hidden card-hover animate-fade-up block" data-testid={`shop-card-${shop.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-50">
        <img src={resolveImageUrl(shop.image)} alt={shop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <SampleBadge url={shop.image} className="absolute top-11 right-3" testid={`shop-sample-${shop.id}`} />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 text-white flex items-end gap-2.5">
          {shop.logo && <img src={resolveImageUrl(shop.logo)} alt="" className="w-10 h-10 rounded-xl object-cover bg-white border-2 border-white shadow flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`shop-card-logo-${shop.id}`} />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider opacity-80">{shop.location}</div>
            <div className="font-display text-lg font-bold leading-tight">{shop.name}</div>
          </div>
        </div>
        {(shop.same_pincode || shop.sponsored || shop.is_sample === true) && (
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1">
            {shop.is_sample === true && (
              <SampleListingBadge testid={`shop-sample-badge-${shop.id}`} />
            )}
            {shop.sponsored && (
              <div className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`shop-sponsored-${shop.id}`}><T k="card.sponsored" /></div>
            )}
            {shop.same_pincode && (
              <div className="bg-emerald-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Same pincode
              </div>
            )}
          </div>
        )}
        <div className="absolute top-3 right-3">
          <ShopStatusPill status={shop.status} shopId={shop.shop_id || shop.id} />
        </div>
      </div>
      <div className="p-4">
        <div className="text-xs text-slate-600 line-clamp-1">{shop.tagline}</div>
        <div className="flex items-center gap-2 mt-2 text-xs">
          <div className="flex items-center gap-0.5 bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded font-semibold">
            <Star className="w-3 h-3 fill-current" /> {shop.rating}
          </div>
          <span className="text-slate-500">{shop.reviews} reviews</span>
          <div className="flex items-center gap-0.5 text-slate-500 ml-auto">
            <MapPin className="w-3 h-3" /> {fmtKm(shop.distance_km)}
          </div>
        </div>
      </div>
    </Link>
  );
}

const applyToJob = (job) => {
  const c = String(job.contact || "").trim();
  if (!c) { toast.info("Employer has not listed application contact details for this job yet."); return; }
  const msg = jobEnquiryMsg(job.company, job.title);
  const d = c.replace(/\D/g, "");
  if (job.apply_method === "email" ? !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c) : d.length < 10 || d.length > 13) { toast.info("Is job ka application contact sahi nahi hai. Kripya employer se seedhe sampark karein."); return; }
  if (job.apply_method === "email") { window.location.href = `mailto:${c}?subject=${encodeURIComponent(`Job enquiry: ${job.title}`)}&body=${encodeURIComponent(msg)}`; return; }
  if (job.apply_method === "phone") { window.location.href = `tel:${d}`; return; }
  window.open(waUrl(d.length === 10 ? `91${d}` : d, msg), "_blank", "noopener");
};

// IST deadline: exact saved date + time (default 23:59).
const deadlineMs = (job) => (/^\d{4}-\d{2}-\d{2}$/.test(job.last_date || "") ? Date.parse(`${job.last_date}T${/^\d{2}:\d{2}$/.test(job.last_time || "") ? job.last_time : "23:59"}:00+05:30`) : NaN);
const istDay = (ms) => new Date(ms + 330 * 60000).toISOString().slice(0, 10);
export function deadlineInfo(job, now = Date.now()) {
  const ms = deadlineMs(job);
  if (Number.isNaN(ms)) return null;
  if (now >= ms) return { expired: true };
  const days = Math.round((Date.parse(job.last_date) - Date.parse(istDay(now))) / 86400000);
  return days <= 2 ? { days } : null;
}
const DAYS_LEFT = ["आज आखिरी दिन", "1 दिन बाकी", "2 दिन बाकी"];
// Owner-facing exact remaining time (IST), only within 2 days of the deadline.
export function expiryLabel(job, now = Date.now()) {
  const ms = deadlineMs(job);
  if (Number.isNaN(ms) || now >= ms) return null;
  const left = ms - now, hrs = Math.floor(left / 3600000), time = job.last_time || "23:59";
  const days = Math.round((Date.parse(job.last_date) - Date.parse(istDay(now))) / 86400000);
  if (days > 2) return null;
  if (left < 3600000) return `${Math.max(1, Math.ceil(left / 60000))} min mein expire hogi`;
  if (days === 0) return `Aaj ${time} IST tak (${hrs} ghante baaki)`;
  if (days === 1) return `Kal expire hogi (${time} IST)`;
  return "2 din baaki";
}

const EXP_LABEL = { fresher: "Fresher accepted", any: "Any experience", experienced: "Experienced candidate" };
const VAC_LABEL = { open: "Hiring Now", paused: "Applications Paused", filled: "Closed / Position Filled", expired: "Expired / समय सीमा समाप्त" };
const fmtDeadline = (job) => (job.last_date ? `${fmtDate(job.last_date)}${job.last_time && job.last_time !== "23:59" ? `, ${job.last_time}` : ""} IST` : "");
const fmtDate = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; } };

function HireDetails({ job, vs }) {
  const salary = jobSalaryText(job);
  const apply = { whatsapp: "WhatsApp", phone: "Phone call", email: "Email" }[job.apply_method] || "WhatsApp";
  const rows = [
    ["Status", VAC_LABEL[vs]], ["Salary", salary], ["Vacancies", job.vacancies],
    ["Qualification", job.qualification], ["Experience", EXP_LABEL[job.experience]], ["Skills", job.skills],
    ["Shift / hours", job.hours], ["Last date to apply", fmtDeadline(job)],
    ["Requirements", job.requirements], ["Instructions", job.instructions], ["How to apply", ["filled", "expired"].includes(vs) ? "Applications closed" : `Directly with employer via ${apply}`],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5" data-testid={`hire-details-${job.id}`}>
      {(job.images || []).length > 1 && <ImageSlider images={job.images} alt={job.title} className="h-44 sm:h-56 rounded-lg bg-slate-100" imgClassName="object-contain" testid={`hire-photos-${job.id}`} />}
      {job.description && <p className="text-xs text-slate-700 whitespace-pre-line break-words">{job.description}</p>}
      <dl className="grid grid-cols-1 sm:grid-cols-[8.5rem_1fr] gap-x-3 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-slate-500 font-medium">{k}</dt>
            <dd className="text-slate-800 break-words" data-testid={`hire-detail-${k.toLowerCase().replace(/[^a-z]+/g, "-")}-${job.id}`}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p className="text-[10px] text-slate-400 pt-1">VerB sirf job ki jaankari dikhata hai. Aavedan aur hiring seedhe employer ke saath hoti hai.</p>
    </div>
  );
}

const jobSalaryText = (job) => job.salary_from != null ? `₹${job.salary_from.toLocaleString()}${job.salary_to != null ? ` - ₹${job.salary_to.toLocaleString()}` : ""} ${job.unit || ""}`.trim() : job.unit === "Negotiable" ? "Negotiable" : "As discussed";

function shareJob(job) {
  const link = `${window.location.origin}/category/hiring?job=${encodeURIComponent(job.id)}`;
  const lines = [`*${job.title}*`, job.company && `Employer: ${job.company}`, `Salary: ${jobSalaryText(job)}`,
    job.pincode && `PIN: ${job.pincode}`, job.last_date && `Last date: ${fmtDeadline(job)}`, "", `Details & apply: ${link}`, "— VerB (Vertual Bazaar)"].filter((x) => x !== undefined && x !== null && x !== false);
  window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
}

function SaveJobHeart({ job }) {
  const { user } = useApp();
  const saved = useSavedJobs(user?.id).has(job.id);
  const onClick = async () => {
    if (!user) { window.dispatchEvent(new Event("vb:open-auth")); return; }
    try { const now = await toggleSavedJob(job.id); toast.success(now ? "Job saved" : "Removed from saved jobs"); }
    catch { toast.error("Could not update saved jobs"); }
  };
  return (
    <button onClick={onClick} aria-pressed={saved} aria-label={saved ? "Remove from saved jobs" : "Save job"} className="p-1.5 -m-1.5 rounded-full hover:bg-rose-50 flex-shrink-0" data-testid={`hire-save-${job.id}`}>
      <Heart className={`w-5 h-5 ${saved ? "fill-rose-500 text-rose-500" : "text-slate-400"}`} />
    </button>
  );
}

export function HireCard({ job, defaultOpen = false }) {
  const [showDetails, setShowDetails] = React.useState(defaultOpen);
  const dl = deadlineInfo(job);
  const vs = dl?.expired && !["filled"].includes(job.vacancy_status) ? "expired" : job.vacancy_status || "open";
  const place = job.work_location || job.location;
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 card-hover animate-fade-up" data-testid={`hire-card-${job.id}`}>
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">{job.image ? <img src={job.image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" /> : <div className="w-14 h-14 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center" data-testid={`hire-noimg-${job.id}`}><Briefcase className="w-6 h-6" /></div>}<SampleBadge url={job.image} className="absolute -bottom-1 left-0 !text-[7px] !px-1" testid={`job-sample-${job.id}`} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-slate-900 leading-snug" data-testid={`hire-title-${job.id}`}>{job.title}</h3>
            <span className="text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{job.type}</span>
            {vs === "paused" && <span className="text-[10px] font-semibold uppercase border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded" data-testid={`hire-status-${job.id}`}>Applications Paused</span>}
            {vs === "filled" && <span className="text-[10px] font-semibold uppercase border border-slate-300 text-slate-700 px-1.5 py-0.5 rounded" data-testid={`hire-status-${job.id}`}>Closed / Position Filled</span>}
            {vs === "expired" && <span className="text-[10px] font-semibold uppercase bg-rose-50 border border-rose-300 text-rose-800 px-1.5 py-0.5 rounded" data-testid={`hire-status-${job.id}`}>Expired / समय सीमा समाप्त</span>}
            {vs === "open" && <span className="text-[10px] font-semibold uppercase border border-emerald-300 text-emerald-800 px-1.5 py-0.5 rounded" data-testid={`hire-status-${job.id}`}>Hiring Now</span>}
            {vs === "open" && dl?.days != null && <span className="text-[10px] font-bold bg-amber-100 border border-amber-400 text-amber-900 px-1.5 py-0.5 rounded" data-testid={`hire-deadline-badge-${job.id}`}>{DAYS_LEFT[dl.days]}</span>}
          </div>
          <div className="text-xs text-slate-600 font-medium truncate">{job.company}</div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-800">
            <span className="font-bold text-emerald-800" data-testid={`hire-salary-${job.id}`}>{jobSalaryText(job)}</span>
            {(place || job.distance_km != null) && <span className="inline-flex items-center gap-1 font-semibold min-w-0" data-testid={`hire-place-${job.id}`}><MapPin className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" /><span className="truncate">{place}</span>{(job.same_pincode || job.distance_km != null) && <span className="flex-shrink-0 whitespace-nowrap" data-testid={`hire-distance-${job.id}`}>· {job.same_pincode ? "same PIN" : `~${job.distance_km < 10 ? job.distance_km.toFixed(1) : Math.round(job.distance_km)} km`}</span>}</span>}
            {job.last_date && <span className="font-semibold" data-testid={`hire-lastdate-${job.id}`}>Last date: {fmtDeadline(job)}</span>}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">Posted {job.posted}</div>
        </div>
        <SaveJobHeart job={job} />
      </div>
      {!showDetails && <p className="text-xs text-slate-600 mt-3 line-clamp-2">{job.description}</p>}
      <div className="mt-2 flex items-center gap-4">
        <button onClick={() => setShowDetails((v) => !v)} aria-expanded={showDetails} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 min-h-[32px]" data-testid={`hire-details-toggle-${job.id}`}>
          {showDetails ? "Details band karein" : "Details dekhein"}
        </button>
        {!["filled", "expired"].includes(vs) && <button onClick={() => shareJob(job)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-emerald-700 min-h-[32px]" data-testid={`hire-share-${job.id}`}>
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>}
      </div>
      {showDetails && <HireDetails job={job} vs={vs} />}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <div className="text-[11px] text-slate-500 min-w-0 truncate">{job.vacancies ? `${job.vacancies} vacanc${job.vacancies > 1 ? "ies" : "y"}` : job.type}</div>
        {["paused", "filled", "expired"].includes(vs) ? (
          <span className="text-xs text-slate-500 font-semibold" data-testid={`apply-closed-${job.id}`}>{vs === "paused" ? "Not accepting now" : vs === "expired" ? "Applications closed" : "Closed"}</span>
        ) : (
          <button className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-4 py-2 font-semibold" data-testid={`apply-${job.id}`} onClick={() => applyToJob(job)}>
            <T k="enq.apply" />
          </button>
        )}
      </div>
    </div>
  );
}
